"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { createClient } from "@/lib/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BottomNav } from "@/components/bottom-nav";
import {
  Loader2,
  MapPin,
  DollarSign,
  Calendar,
  Users,
  Plus,
  Clock,
  CheckCircle2,
  XCircle,
  Briefcase,
} from "lucide-react";
import Link from "next/link";
import { TRADE_CATEGORIES } from "@/lib/constants";

const TRADE_LABEL: Record<string, string> = TRADE_CATEGORIES.reduce((acc, cat) => {
  acc[cat.id] = cat.label;
  return acc;
}, {} as Record<string, string>);

interface JobWithCounts {
  id: string;
  title: string;
  trade: string;
  suburb: string | null;
  rate_min: number | null;
  rate_max: number | null;
  rate_type: string | null;
  start_date: string | null;
  duration: string | null;
  workers_needed: number;
  status: string;
  created_at: string;
  description: string;
  site_address: string | null;
  certifications_required: string[];
  matchCount: number;
  hiredCount: number;
}

const statusConfig: Record<string, { label: string; color: string; icon: typeof Clock }> = {
  active: { label: "Active", color: "bg-green-100 text-green-800", icon: Clock },
  filled: { label: "Filled", color: "bg-blue-100 text-blue-800", icon: CheckCircle2 },
  closed: { label: "Closed", color: "bg-gray-100 text-gray-800", icon: XCircle },
  draft: { label: "Draft", color: "bg-yellow-100 text-yellow-800", icon: Clock },
};

export default function ContractorMyJobsPage() {
  const router = useRouter();
  const supabase = createClient();
  const [jobs, setJobs] = useState<JobWithCounts[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingJob, setEditingJob] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: company } = await supabase
        .from("companies")
        .select("id")
        .eq("profile_id", user.id)
        .single();

      if (!company) {
        setLoading(false);
        return;
      }

      // Fetch jobs with match counts
      const { data: jobsData } = await supabase
        .from("jobs")
        .select("id, title, trade, suburb, rate_min, rate_max, rate_type, start_date, duration, workers_needed, status, created_at, description, site_address, certifications_required")
        .eq("company_id", company.id)
        .order("created_at", { ascending: false });

      if (!jobsData || jobsData.length === 0) {
        setJobs([]);
        setLoading(false);
        return;
      }

      // Get match counts per job
      const jobIds = jobsData.map(j => j.id);
      const { data: matches } = await supabase
        .from("matches")
        .select("job_id, status")
        .in("job_id", jobIds);

      const matchCounts = new Map<string, { total: number; hired: number }>();
      for (const m of (matches || [])) {
        const counts = matchCounts.get(m.job_id) || { total: 0, hired: 0 };
        counts.total++;
        if (m.status === "hired") counts.hired++;
        matchCounts.set(m.job_id, counts);
      }

      setJobs(jobsData.map(j => ({
        ...j,
        matchCount: matchCounts.get(j.id)?.total || 0,
        hiredCount: matchCounts.get(j.id)?.hired || 0,
      })));
      setLoading(false);
    }
    load();
  }, [supabase]);

  function formatRate(job: JobWithCounts): string | null {
    if (!job.rate_min && !job.rate_max) return null;
    const suffix = job.rate_type === "hourly" ? "/hr" : "/day";
    if (job.rate_min && job.rate_max && job.rate_min !== job.rate_max) {
      return `$${job.rate_min}–$${job.rate_max}${suffix}`;
    }
    return `$${job.rate_min || job.rate_max}${suffix}`;
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      <main className="mx-auto max-w-screen-sm px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold">My Jobs</h1>
          <Button asChild size="sm" className="h-10">
            <Link href="/contractor/post-job">
              <Plus className="mr-1.5 h-4 w-4" />
              Post Job
            </Link>
          </Button>
        </div>

        {jobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Briefcase className="h-16 w-16 text-muted-foreground/50" />
            <p className="mt-4 text-xl font-bold">No jobs posted yet</p>
            <p className="mt-2 text-base text-muted-foreground">
              Post your first job to start finding workers.
            </p>
            <Button asChild className="mt-8 h-14 px-8 text-lg font-semibold">
              <Link href="/contractor/post-job">Post a Job</Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {jobs.map((job) => {
              const status = statusConfig[job.status] || statusConfig.active;
              const StatusIcon = status.icon;
              const rate = formatRate(job);

              return (
                <Card
                  key={job.id}
                  className="cursor-pointer hover:bg-accent/50 active:scale-[0.99] transition-all"
                  onClick={() => router.push(`/contractor/my-jobs/${job.id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="secondary" className="text-xs">
                            {TRADE_LABEL[job.trade] || job.trade}
                          </Badge>
                          <Badge className={`text-xs ${status.color}`}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {status.label}
                          </Badge>
                        </div>
                        <h3 className="font-semibold text-base truncate">{job.title}</h3>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-muted-foreground">
                      {job.suburb && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5" /> {job.suburb}
                        </span>
                      )}
                      {rate && (
                        <span className="flex items-center gap-1 font-medium text-foreground">
                          <DollarSign className="h-3.5 w-3.5" /> {rate}
                        </span>
                      )}
                      {job.start_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          {new Date(job.start_date).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}
                        </span>
                      )}
                    </div>

                    {/* Match/hire summary */}
                    <div className="mt-3 flex items-center gap-4 text-sm">
                      <span className="flex items-center gap-1.5">
                        <Users className="h-4 w-4 text-blue-600" />
                        <span className="font-medium">{job.matchCount}</span> match{job.matchCount !== 1 ? "es" : ""}
                      </span>
                      {job.hiredCount > 0 && (
                        <span className="flex items-center gap-1.5 text-green-700">
                          <CheckCircle2 className="h-4 w-4" />
                          <span className="font-medium">{job.hiredCount}</span> hired
                        </span>
                      )}
                      <span className="text-muted-foreground">
                        Need {job.workers_needed}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
      <BottomNav userType="contractor" />
    </div>
  );
}
